<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required, email, numeric, minLength, maxLength  } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'
import { onMounted } from 'vue'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { useAuthStore } from '@/stores/useAuthStore'
import SSNInput from './SSNInput.vue'

const props = defineProps<{
  disable?: boolean
}>()

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const rules = {
  umsokn: {
    address: { required },
    email: { required, email },
    postalCode: { required },
    phone: { required, numeric, minLength: minLength(7), maxLength: maxLength(15) }
  }
}

defineExpose({ validate })

const v$ = useVuelidate(rules, { umsokn })

onMounted(async () => {
  const authStore = useAuthStore()
  const user = await authStore.getAuthUser()
  if (user) {
    onLoaded(() => {
      if (umsokn.value) {
        umsokn.value.name = user.name
        umsokn.value.ssn = user.username
      }
    })
  }
})

function validate() {
  v$.value.$touch()
  return !v$.value.$invalid
}

</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">{{ t('umsokn_umsaekjandi_header') }}</h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.name"
          :label="t('umsokn_umsaekjandi_nafn')"
          readonly
          variant="outlined"
          density="comfortable"
        />
      </v-col>

      <v-col cols="12" md="6">
        <SSNInput
          v-model="umsokn.ssn"
          :label="t('umsokn_umsaekjandi_kennitala')"
          readonly
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.address"
          :label="t('umsokn_umsaekjandi_heimilisfang')"
          :readonly="props.disable"
          variant="outlined"
          density="comfortable"
          :error-messages="v$.umsokn.address.$errors.map(() => t('umsokn_umsaekjandi_heimilisfang_error'))"
        />
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.postalCode"
          :label="t('umsokn_umsaekjandi_postnumer')"
          :readonly="props.disable"
          variant="outlined"
          density="comfortable"
          :error-messages="v$.umsokn.postalCode.$errors.map(() => t('umsokn_umsaekjandi_postnumer_error'))"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.email"
          :label="t('umsokn_umsaekjandi_netfang')"
          :readonly="props.disable"
          variant="outlined"
          density="comfortable"
          :error-messages="
          v$.umsokn.email.$error
            ? [
                v$.umsokn.email.required.$invalid
                  ? t('umsokn_umsaekjandi_netfang_error')
                  : '',
                v$.umsokn.email.email.$invalid
                  ? t('umsokn_sereignbreyting_step1_netfang_invalid')
                  : ''
              ].filter(Boolean)
            : []
          "
        />
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.phone"
          :label="t('umsokn_umsaekjandi_simanumer')"
          :readonly="props.disable"
          variant="outlined"
          density="comfortable"
          :error-messages="
          v$.umsokn.phone.$error
            ? [
                v$.umsokn.phone.required.$invalid
                  ? t('umsokn_umsaekjandi_simanumer_error')
                  : '',
                v$.umsokn.phone.numeric.$invalid
                  ? t('umsokn_umsaekjandi_simanumer_invalid')
                  : '',
                v$.umsokn.phone.minLength.$invalid || v$.umsokn.phone.maxLength.$invalid
                  ? t('umsokn_umsaekjandi_simanumer_invalid')
                  : ''
              ].filter(Boolean)
            : []
          "
        />
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
input {
  width: 100%;
}
.forms__fadein {
  opacity: 0;
  transition: opacity 0.4s ease-in-out;
}
.forms__fadeout {
  opacity: 1;
}
.slideup,
.slidedown {
  max-height: 0;
  overflow: hidden;
  width: 100%;
  transition: max-height 0.8s ease-in-out;
}
.slidedown {
  max-height: 500px;
}
</style>
